/**
 * The write side of the page: decisions, instructions, "seen" and Resend nudge.
 *
 * Everything that talks to the server lives here so the controls stay dumb and
 * testable. Rules it keeps:
 *   - a decision is sent with an Idempotency-Key; a retry after a failure reuses
 *     the same key, so a lost response can never write a second decision;
 *   - the chip changes straight away (optimistic) and is rolled back, with a
 *     visible error and a Retry, if the server says no;
 *   - after any write the snapshot is refetched, because the server's overlay
 *     (decisions, queue status, threads) rides on it;
 *   - while any composer has text, `onHoldChange(true)` lets the page hold new
 *     snapshots back so a refresh cannot eat what is being typed.
 */

import React from 'react';
import { BotOpsHttpError } from './api/botOpsClient';
import { ChatRequest, InstructionInput, markInstructionSeen, nudgeInstruction, postDecision, postInstruction, retryChatPost } from './api/botOpsService';
import { DecisionValue, Instruction } from './lib/types';

export interface DecisionUi {
  /** What the chip shows right now, ahead of the server. */
  optimistic: DecisionValue | null;
  saving: boolean;
  error: string | null;
}

export type SendResult = { ok: true; instruction: Instruction } | { ok: false; error: string; status: number };

export interface BotOpsActionsApi {
  canWrite: boolean;
  decisionUi: (actionId: string) => DecisionUi;
  decide: (actionId: string, value: 'yes' | 'no' | 'pending', chat?: ChatRequest | null) => Promise<boolean>;
  retryChat: (actionId: string) => Promise<{ ok: boolean; message: string }>;
  retryDecision: (actionId: string) => Promise<boolean>;
  dismissDecisionError: (actionId: string) => void;
  send: (input: InstructionInput) => Promise<SendResult>;
  markSeen: (id: string) => Promise<void>;
  nudge: (id: string) => Promise<{ ok: boolean; message: string }>;
  setComposing: (composerId: string, dirty: boolean) => void;
}

const IDLE: DecisionUi = { optimistic: null, saving: false, error: null };

const Ctx = React.createContext<BotOpsActionsApi | null>(null);

export function useBotOpsActions(): BotOpsActionsApi {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useBotOpsActions must be used inside <BotOpsActionsProvider>');
  return v;
}

const newKey = () => {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c && c.randomUUID ? c.randomUUID() : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const friendly = (e: unknown): { message: string; status: number } => {
  if (e instanceof BotOpsHttpError) {
    if (e.status === 429) return { message: `Too many changes. Try again in ${e.retryAfter || 30} s.`, status: 429 };
    if (e.status === 403) return { message: 'Only the super admin can make changes here.', status: 403 };
    if (e.status === 0) return { message: 'Could not reach the server. Check your connection and retry.', status: 0 };
    return { message: e.message, status: e.status };
  }
  return { message: (e as Error)?.message || 'Something went wrong', status: 0 };
};

export function BotOpsActionsProvider({
  canWrite, refresh, onHoldChange, children,
}: {
  canWrite: boolean;
  refresh: () => Promise<void> | void;
  onHoldChange?: (hold: boolean) => void;
  children: React.ReactNode;
}) {
  const [ui, setUi] = React.useState<Record<string, DecisionUi>>({});
  // The key of the last attempt per action, kept until it succeeds, so Retry reuses it.
  const keys = React.useRef<Map<string, { value: string; key: string; chat: ChatRequest | null }>>(new Map());
  const inFlight = React.useRef<Set<string>>(new Set());
  const composing = React.useRef<Set<string>>(new Set());
  const refreshRef = React.useRef(refresh);
  refreshRef.current = refresh;
  const holdCb = React.useRef(onHoldChange);
  holdCb.current = onHoldChange;
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const patch = React.useCallback((id: string, p: Partial<DecisionUi>) => {
    if (!alive.current) return;
    setUi((u) => ({ ...u, [id]: { ...(u[id] || IDLE), ...p } }));
  }, []);

  const run = React.useCallback(async (actionId: string, value: 'yes' | 'no' | 'pending', chat: ChatRequest | null = null) => {
    if (inFlight.current.has(actionId)) return false; // a double click is one request
    inFlight.current.add(actionId);
    let attempt = keys.current.get(actionId);
    if (!attempt || attempt.value !== value) {
      attempt = { value, key: newKey(), chat };
      keys.current.set(actionId, attempt);
    } else {
      attempt.chat = chat; // same key, same decision: a replay returns the original, a lost request carries the latest choice
    }
    patch(actionId, { optimistic: value, saving: true, error: null });
    try {
      await postDecision({ actionId, value, idempotencyKey: attempt.key, gchat: attempt.chat });
      keys.current.delete(actionId);
      await refreshRef.current();
      patch(actionId, { optimistic: null, saving: false, error: null });
      return true;
    } catch (e) {
      patch(actionId, { optimistic: null, saving: false, error: friendly(e).message });
      return false;
    } finally {
      inFlight.current.delete(actionId);
    }
  }, [patch]);

  // These only read refs, so their identity never changes: effects that list them as dependencies do not re-run.
  const retryDecision = React.useCallback(async (id: string) => {
    const k = keys.current.get(id);
    return k ? run(id, k.value as 'yes' | 'no' | 'pending', k.chat) : false;
  }, [run]);
  const dismissDecisionError = React.useCallback((id: string) => { keys.current.delete(id); patch(id, { error: null }); }, [patch]);
  const send = React.useCallback(async (input: InstructionInput): Promise<SendResult> => {
    try {
      const r = await postInstruction(input);
      await refreshRef.current();
      return { ok: true, instruction: (r.data as { instruction: Instruction }).instruction };
    } catch (e) {
      const f = friendly(e);
      return { ok: false, error: f.message, status: f.status };
    }
  }, []);
  const markSeen = React.useCallback(async (id: string) => {
    try { await markInstructionSeen(id); await refreshRef.current(); } catch { /* the badge simply stays until the next poll */ }
  }, []);
  const nudge = React.useCallback(async (id: string) => {
    try {
      const r = await nudgeInstruction(id);
      const status = r.data && r.data.webhook ? r.data.webhook.status : null;
      await refreshRef.current();
      return status === 'sent'
        ? { ok: true, message: 'Nudge sent. Grok Bot should acknowledge within a minute.' }
        : { ok: false, message: 'Could not reach Grok Bot\u2019s webhook yet. The message is safe in the queue and will be retried.' };
    } catch (e) {
      return { ok: false, message: friendly(e).message };
    }
  }, []);
  const retryChat = React.useCallback(async (id: string) => {
    try {
      await retryChatPost(id);
      await refreshRef.current();
      return { ok: true, message: 'Chat post queued again.' };
    } catch (e) {
      return { ok: false, message: friendly(e).message };
    }
  }, []);
  const setComposing = React.useCallback((composerId: string, dirty: boolean) => {
    const had = composing.current.size > 0;
    if (dirty) composing.current.add(composerId); else composing.current.delete(composerId);
    const has = composing.current.size > 0;
    if (had !== has && holdCb.current) holdCb.current(has);
  }, []);

  const api = React.useMemo<BotOpsActionsApi>(() => ({
    canWrite,
    decisionUi: (id) => ui[id] || IDLE,
    decide: run,
    retryChat,
    retryDecision,
    dismissDecisionError,
    send,
    markSeen,
    nudge,
    setComposing,
  }), [canWrite, ui, run, retryChat, retryDecision, dismissDecisionError, send, markSeen, nudge, setComposing]);

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}
